import * as Sentry from '@sentry/nextjs'
import { IBackEndError, IUnknownObject } from '../store/data'

// use to set which errors to log in sentry
const SENTRY_CONFIG = {
  ERROR: true,
  LOG: false,
}

const BENIGN_ERROR_PATTERNS = [
  /attempted to hard navigate to the same url/i,
  /cancel rendering route/i,
  /window\.webkit\.messageHandlers/i,
  /triggerEvent/i,
  /runtime\.sendMessage\(\)\. Tab not found/i,
  /cross-origin frame/i,
  /permission denied to get property "href" on cross-origin object/i,
  // Benign browser/runtime quirks — not actionable application bugs
  /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/i,
  /Non-Error promise rejection captured/i,
  /^AxiosError:?[ ]+(Network Error|Request aborted)$/i,
  /^CanceledError:?[ ]+canceled$/i,
]

const isBenignSentryError = (input: unknown) => {
  if (input && typeof input === 'object') {
    const error = input as IUnknownObject
    const code = String(error.code ?? '')
    const message = String(error.message ?? '')

    if (
      code === 'ERR_CANCELED' ||
      code === 'ERR_NETWORK' ||
      (code === 'ECONNABORTED' && message === 'Request aborted')
    ) {
      return true
    }
  }

  const text = input instanceof Error ? `${input.name} ${input.message}` : String(input ?? '')
  return BENIGN_ERROR_PATTERNS.some((pattern) => pattern.test(text))
}

interface SentryErrorEvent {
  exception?: {
    values?: Array<{
      stacktrace?: {
        frames?: Array<{ filename?: string; function?: string }>
      }
      type?: string
      value?: string
    }>
  }
}

// Sentry's Next.js integration rewrites every frame URL to `app:///<path>`, so a
// frame that is just `app:///` (optionally followed by a query string) means the
// code was attributed to the page URL itself rather than to a JS file. This app
// is a static export whose only inline <script> is the non-executable
// __NEXT_DATA__ JSON blob and whose bundles all live under `/_next/static/`, so
// our own code can never produce such a frame. It shows up when someone else
// evaluates JavaScript inside the page: in-app browsers (Google app / Chrome on
// iOS inject their own scripts — BOOKING-FORM-22F), Google Tag Manager custom
// HTML tags (the recursive stack overflow in BOOKING-FORM-22E / 22G — its
// minified function names change with every GTM release, so matching on them
// is not durable) or browser extensions. `allowUrls` cannot catch these because
// the page URL is on our own domain.
const INJECTED_SCRIPT_FRAME = /^app:\/\/\/(?:[?#].*)?$/
const ANONYMOUS_FRAME_FILENAMES = new Set(['<anonymous>', '[native code]'])

const isInjectedScriptError = (event: SentryErrorEvent) => {
  const frames = (event.exception?.values || []).flatMap(
    (exception) => exception.stacktrace?.frames || []
  )

  let injectedFrames = 0
  for (const frame of frames) {
    const filename = frame.filename || ''

    if (INJECTED_SCRIPT_FRAME.test(filename)) {
      injectedFrames += 1
    } else if (filename && !ANONYMOUS_FRAME_FILENAMES.has(filename)) {
      // At least one frame comes from a real file (our bundle or a third-party
      // script) — leave the decision to allowUrls / denyUrls.
      return false
    }
  }

  return injectedFrames > 0
}

// Only report errors whose stack frames originate from our own booking-form
// domains. Errors injected by browser extensions, third-party scripts or
// partner-site widgets are dropped before they ever reach Sentry.
//   - rezervace.spabooking.cz / reservierung.spabooking.de
//   - rezervace.frantiskovylazne.cz  (+ their staging.* variants)
const ALLOW_URLS = [
  /https?:\/\/([a-z0-9-]+\.)*spabooking\.(cz|de)/i,
  /https?:\/\/([a-z0-9-]+\.)*frantiskovylazne\.cz/i,
]

// Never report errors injected by browser extensions / non-web schemes.
const DENY_URLS = [
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-(web-)?extension:\/\//i,
  /extensions\//i,
]

// Deploy stage derived from the host at runtime — this app is a static export,
// so there is no build-time server environment to read. Lets Sentry alerts
// target a single stage (e.g. only `production`).
const resolveEnvironment = () => {
  if (typeof window === 'undefined') return 'unknown'
  const host = window.location.hostname
  if (host === 'localhost' || host === '127.0.0.1') return 'development'
  if (host.includes('staging')) return 'staging'
  return 'production'
}

// Which booking site this build serves (spabookingCz / spabookingDe / fla ...).
const SITE = process.env.NEXT_PUBLIC_LAYOUT_NAME || 'unknown'

export const MESSAGE_LEVELS = {
  CRITICAL: 'critical',
  DEBUG: 'debug',
  ERROR: 'error',
  FATAL: 'fatal',
  INFO: 'info',
  LOG: 'log',
  WARNING: 'warning',
}

export const initSentry = () => {
  Sentry.init({
    // Report only errors coming from our own domains; drop browser-extension
    // and injected-script noise (see ALLOW_URLS / DENY_URLS above).
    allowUrls: ALLOW_URLS,
    beforeSend(event, hint) {
      const originalException = hint.originalException

      if (isBenignSentryError(originalException)) {
        return null
      }

      const exceptionValue = event.exception?.values?.[0]?.value
      if (exceptionValue && isBenignSentryError(exceptionValue)) {
        return null
      }

      if (isInjectedScriptError(event)) {
        return null
      }

      return event
    },
    denyUrls: DENY_URLS,
    dsn: 'https://52972f2ed41543429c2fef4ac8e95212@o4504203360534528.ingest.sentry.io/4504282529726464',
    environment: resolveEnvironment(),
  })

  Sentry.configureScope(function (scope) {
    // @ts-ignore known issue in sentry
    scope.setLevel(MESSAGE_LEVELS.ERROR)
    // Tag every event with the booking site so alerts can be filtered per site.
    scope.setTag('site', SITE)
  })
}

export const alertSentryOnAPIError = (error: IUnknownObject) => {
  if (!SENTRY_CONFIG.ERROR) return
  if (isBenignSentryError(error)) return

  const { config, level = MESSAGE_LEVELS.ERROR, response } = error || {}
  const errorCode = String(response?.status || 422)
  const method = config?.method || 'none'
  const path = config?.url || window.location.href

  if (response?.status >= 400 && response?.status < 500) {
    return
  }

  Sentry.withScope((scope) => {
    // @ts-ignore known issue in sentry
    scope.setLevel(level)
    if (config?.data) scope.setExtras(JSON.parse(config.data))

    if (response?.data?.errors) {
      response.data.errors.forEach((error: IBackEndError, i: number) => {
        Sentry.setContext(`BE_error_stack_${i}`, buildErrorStack(error))
      })
    }

    // for error grouping purposes
    scope.setFingerprint([method, path, errorCode])
    Sentry.captureException(error)
  })
}

const buildErrorStack = ({ field, message }: IBackEndError) => {
  const stack: IUnknownObject = {}

  if (field) stack['field'] = field
  if (message) {
    stack['message'] = Array.isArray(message) ? message.join(` // `) : String(message)
  }

  return stack
}

export const customizeError = (error: IUnknownObject, modifications: {}) =>
  Object.assign(error, modifications)

export const logToSentry = (loggedText: string, payload: IUnknownObject) => {
  if (!SENTRY_CONFIG.LOG) return

  Sentry.withScope((scope) => {
    // @ts-ignore known issue in sentry
    scope.setLevel(MESSAGE_LEVELS.LOG)
    if (payload) scope.setExtras(payload)

    Sentry.captureMessage(loggedText)
  })
}
